 
import React, { useCallback, useEffect, useRef, useState } from 'react'
// @ts-expect-error TS7016 -- MIGRATION: not approved, fix when encountered
import { InputText } from 'ui_kit_qmd'
import { useSelector } from 'store/hooks/useSelector'
import { text } from '../../utils'
import { getPharmacyAddressValue } from '../../../../../../../../utils/common'
import { PharmacyDropdownItem } from '../../../../../../../pharmacy-dropdown-item/PharmacyDropdownItem'
import { type Address, type Pharmacy } from '../../../../../../../../types/types'
import { useProductOrderContext } from '../../../../../../context'
import { adaptationSymbolsForAPI, pharmacyFilters, searchPharmacies } from './utils'
import { PharmacyFilters } from '../../../../../../../pharmacy-filter/PharmacyFilter'
import { useAutoUpdatingRef } from '@quickmd-llc/frontend-utils'
import { selectPharmacySearchDaw } from '../../../../../../../../store/flags/FlagsSelectors'
import { usePharmacyV2Search } from 'hooks/usePharmacyV2Search'
import { PharmacyZipFilterRow } from 'components/pharmacy-zip-filter-row/PharmacyZipFilterRow'

interface Props {
  setSelectedAddress: (address: Address) => void
}
const inputFullContainerStyle = { width: '100%' }
export const PharmacySearch = ({ setSelectedAddress }: Props) => {
  const { pharmacy, setPharmacy, patientId, orderInfo, setOrderInfo } = useProductOrderContext()
  const isPharmacySearchDaw = useSelector(selectPharmacySearchDaw)
  const patientAddressZip = useSelector((state) => state.chartsReducer?.patientInfo?.[patientId]?.Address?.Zip) ?? ''
  const emptyResultsRef = useRef<Pharmacy[] | undefined>()
  const inputRef = useRef<HTMLBodyElement>()
  const [searchError, setSearchError] = useState('')
  const [isPharmacyInputFocused, setIsPharmacyInputFocused] = useState(false)
  const [filters, setFilters] = useState(pharmacyFilters)
  const [searchValue, setSearchValue] = useState<string | null>(() =>
    pharmacy ? getPharmacyAddressValue(pharmacy) : '',
  )
  const zipInputFocusedRef = useRef(false)

  const {
    pharmacies: v2Pharmacies,
    pharmacySearchZip,
    setPharmacySearchZip,
  } = usePharmacyV2Search({
    patientId,
    searchInput: searchValue ?? '',
    patientZip: patientAddressZip,
    // @ts-expect-error TS2322 -- MIGRATION: not approved, fix when encountered
    enabled: isPharmacySearchDaw,
  })

  const [pharmacies, setPharmacies] = useState<Pharmacy[]>(() => [pharmacy].filter(Boolean))
  useEffect(() => {
    if (isPharmacySearchDaw) return
    const fetchPharmacies = async () => {
      try {
        const query = searchValue?.trim() || ''
        const adaptedQuery = adaptationSymbolsForAPI(query)
        const result = await searchPharmacies(patientId, adaptedQuery, filters)
        setPharmacies(result || [])
        if (!query) emptyResultsRef.current = result || undefined
      } catch (e) {
        console.error(`Error fetching pharmacies:`, e)
      }
    }
    if (pharmacy) {
      setPharmacies([pharmacy])
      if (!searchValue?.trim()) {
        setSearchValue(getPharmacyAddressValue(pharmacy))
      }
    // @ts-expect-error TS2532 -- MIGRATION: not approved, fix when encountered
    } else if (searchValue?.trim().length > 2 && isPharmacyInputFocused) {
      const debounceTimer = setTimeout(() => fetchPharmacies(), 500)
      return () => clearTimeout(debounceTimer)
    } else if (emptyResultsRef.current) {
      setPharmacies(emptyResultsRef.current)
    } else {
      fetchPharmacies()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue, filters, isPharmacySearchDaw])

  const displayPharmacies = isPharmacySearchDaw ? v2Pharmacies : pharmacies
  const onFocusChange = useCallback((focused: boolean) => {
    setTimeout(() => setIsPharmacyInputFocused(focused), 400)
  }, [])
  const onSearchChange = useCallback(
    (value: string) => {
      setPharmacy(undefined)
      setSearchValue(value)
      setSearchError('')
    },
    [setPharmacy],
  )
  const onSelectPharmacy = useCallback(
    (selected: Pharmacy) => {
      setPharmacy(selected)
      setOrderInfo({ ...orderInfo, Address: selected.Address })
      setSearchValue(getPharmacyAddressValue(selected))
      setSelectedAddress(selected.Address)
    },
    [orderInfo, setOrderInfo, setPharmacy, setSelectedAddress],
  )
  const onSelectFilter = useCallback((name: string) => {
    setFilters((prevFilters) =>
      prevFilters.map((filter, idx) => {
        const updatedFilter = { ...filter }
        if (name === 'All') {
          updatedFilter.isActive = idx === 0
        } else if (filter.name === name) updatedFilter.isActive = !filter.isActive
        return updatedFilter
      }),
    )
    emptyResultsRef.current = undefined
  }, [])
  const formatPharmacyAddress = (selectedPharmacy: Pharmacy): string => {
    const { StreetAddress1, StreetAddress2, City, StateCode, Zip, CountryCode } = selectedPharmacy?.Address || {}
    return [StreetAddress1, StreetAddress2, City, StateCode, Zip, CountryCode].filter(Boolean).join(', ')
  }

  const pharmacyRef = useAutoUpdatingRef(pharmacy)
  const searchValueRef = useAutoUpdatingRef(searchValue)

  // we were having issues where the pharmacy and the input were out of sync. any time the input is not focused,
  // make sure to sync up the input and the pharmacy. if we close and nothing is in the input, clear the pharmacy.
  // if we close and there isn't a pharmacy, clear the input. zipInputFocusedRef skips this when the blur was
  // caused by interacting with the zip filter row, so the dropdown survives clicks inside it.
  React.useEffect(() => {
    if (!isPharmacyInputFocused) {
      if (!zipInputFocusedRef.current) {
        if (!pharmacyRef.current) setSearchValue('')
        if (!searchValueRef.current) setPharmacy(undefined)
      }
      zipInputFocusedRef.current = false
    }
  }, [isPharmacyInputFocused, pharmacyRef, searchValueRef, setPharmacy, setSearchValue])
  return (
    <div className='edit-mode-account-information-view-pharmacy-input-main-wrapper'>
      <InputText
        className={
          // @ts-expect-error TS18047 -- MIGRATION: not approved, fix when encountered
          pharmacy && !isPharmacyInputFocused && searchValue.slice(0, searchValue.indexOf('-')).includes('Walmart') ?
            'pharmacy-warning'
          : ''
        }
        id='pharmacy-input'
        ref={inputRef}
        label={text.PREFERRED_PHARMACY_TEXT}
        onSetIsFocused={onFocusChange}
        containerStyle={inputFullContainerStyle}
        setValue={onSearchChange}
        value={searchValue}
        // @ts-expect-error TS18047 -- MIGRATION: not approved, fix when encountered
        showWarning={pharmacy && !isPharmacyInputFocused ? searchValue.includes('Walmart') : null}
        localError={searchError}
        view='visitType'
        isSearch
      />
      {isPharmacyInputFocused && !searchError ? <div className='edit-pharmacy-list-of-pharmacies-dropdown'>
          {isPharmacySearchDaw ?
            <PharmacyZipFilterRow
              zipCode={pharmacySearchZip}
              patientZipFallback={patientAddressZip}
              onSave={setPharmacySearchZip}
              onInteract={() => {
                zipInputFocusedRef.current = true
              }}
            />
          : <PharmacyFilters onSelectFilter={onSelectFilter} pharmacyFilters={filters} />}
          {displayPharmacies?.length > 0 ?
            displayPharmacies.map((pharmacyItem, idx) => (
              <PharmacyDropdownItem
                key={pharmacyItem.PharmacyId}
                currentPharmacy={pharmacyItem}
                title={pharmacyItem.Name}
                // @ts-expect-error TS2345 -- MIGRATION: not approved, fix when encountered
                description={formatPharmacyAddress(pharmacyItem)}
                distance={pharmacyItem.DistanceMiles}
                onSelectPharmacy={onSelectPharmacy}
                isTheLastItem={idx === displayPharmacies.length - 1}
                isEmpty={false}
                // @ts-expect-error TS18047 -- MIGRATION: not approved, fix when encountered
                showWarning={pharmacyItem.Name.includes('Walmart')}
              />
            ))
          : <PharmacyDropdownItem
              key='no-results'
              currentPharmacy={null}
              title='No results'
              description={null}
              isEmpty
              isTheLastItem
              distance={undefined}
              onSelectPharmacy={undefined}
              showWarning={false}
            />
          }
        </div> : null}
      {/*
       // @ts-expect-error TS18047 -- MIGRATION: not approved, fix when encountered */}
      {pharmacy && searchValue.slice(0, searchValue.indexOf('-')).includes('Walmart') ?
        <div className='pharmacy-search-warning'>
          Patients often experience issues filling buprenorphine prescriptions at Walmart pharmacies.
        </div>
      : null}
    </div>
  )
}
